import * as THREE from "three";
import { createRng } from "../rng";
import { buildCharacter, type Character } from "./character";
import type { Ground } from "../world/walkable";
import type { PeerState } from "../net/session";
import { PARTY } from "../config";

const LABEL_CSS = `
#nameplates {
  position: fixed;
  inset: 0;
  z-index: 9;
  pointer-events: none;
  overflow: hidden;
}
.nameplate {
  position: absolute;
  transform: translate(-50%, -100%);
  padding: 2px 7px 3px;
  background: rgba(9, 8, 7, 0.82);
  border-radius: 2px;
  color: #e9e2d0;
  font-family: "Silkscreen", monospace;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  opacity: 0.92;
}
`;

let labelsRoot: HTMLDivElement | null = null;

function ensureLabels(): HTMLDivElement {
  if (labelsRoot) return labelsRoot;
  const style = document.createElement("style");
  style.textContent = LABEL_CSS;
  document.head.appendChild(style);
  const root = document.createElement("div");
  root.id = "nameplates";
  document.body.appendChild(root);
  labelsRoot = root;
  return root;
}

export interface RemotePlayer {
  readonly root: THREE.Group;
  readonly character: Character;
  name: string;
  hasTorch: boolean;
  sync(delta: number, peer: PeerState, ground: Ground, daylight: number): void;
  project(camera: THREE.Camera, width: number, height: number): void;
  dispose(): void;
}

const shortestAngle = (from: number, to: number): number =>
  ((to - from + Math.PI * 3) % (Math.PI * 2)) - Math.PI;

export function buildRemote(peer: PeerState, scale = 1): RemotePlayer {
  const rig = buildCharacter(createRng(hash(peer.id)));
  const root = new THREE.Group();
  root.add(rig.root);

  const label = document.createElement("div");
  label.className = "nameplate";
  label.textContent = peer.name;
  ensureLabels().appendChild(label);

  const position = new THREE.Vector3(peer.x, 0, peer.z);
  let yaw = peer.yaw;
  let speed = 0;
  let footY = 0;
  const projected = new THREE.Vector3();

  const remote: RemotePlayer = {
    root,
    character: rig,
    name: peer.name,
    hasTorch: false,
    sync(delta: number, next: PeerState, ground: Ground, daylight: number): void {
      const blend = 1 - Math.exp(-delta / 0.07);
      position.x += (next.x - position.x) * blend;
      position.z += (next.z - position.z) * blend;
      yaw += shortestAngle(yaw, next.yaw) * blend;
      speed += (next.speed - speed) * blend;

      const target = ground.heightAt(position.x, position.z);
      footY += (target - footY) * (1 - Math.exp(-delta / 0.05));

      root.position.set(position.x, footY + rig.footOffset, position.z);
      root.rotation.y = yaw;
      rig.update(delta, speed, performance.now() / 1000);

      if (next.name !== remote.name) {
        remote.name = next.name;
        label.textContent = next.name;
      }

      if (next.torch !== remote.hasTorch) {
        remote.hasTorch = next.torch;
        rig.setTorchCarried(next.torch);
      }
      rig.torchGlow(daylight);
    },
    project(camera: THREE.Camera, width: number, height: number): void {
      projected.set(position.x, footY + PARTY.headAnchor, position.z);
      projected.project(camera);
      const visible =
        projected.z < 1 &&
        Math.abs(projected.x) < PARTY.onScreen &&
        Math.abs(projected.y) < PARTY.onScreen;
      if (!visible) {
        label.style.display = "none";
        return;
      }
      label.style.display = "";
      label.style.left = `${((projected.x * 0.5 + 0.5) * width).toFixed(1)}px`;
      label.style.top = `${((-projected.y * 0.5 + 0.5) * height).toFixed(1)}px`;
    },
    dispose(): void {
      root.removeFromParent();
      label.remove();
    },
  };

  // scale to match the local character's proportions
  root.scale.setScalar(scale);
  root.position.set(position.x, 0, position.z);
  return remote;
}

function hash(value: string): number {
  let result = 2166136261;
  for (let i = 0; i < value.length; i++) {
    result ^= value.charCodeAt(i);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}
